import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { traceClient } from '@terra/shared-rest-clients-trace';
import { getCurrentGatewayUrl } from './environmentDetection';
import type { BackendRunningNeoTrace } from './neoAttackReconciliation';
import { NEO_ATTACK_RUNNING_FETCH_LIMIT } from './neoAttackStatus';

function mapRunningNeoTraces(
  traces: TracePreviewDto[],
): BackendRunningNeoTrace[] {
  return traces
    .filter(
      (trace): trace is TracePreviewDto & { signalId: string } =>
        typeof trace.signalId === 'string' && trace.signalId.length > 0,
    )
    .map((trace) => ({
      signalId: trace.signalId,
      threadId: trace.threadId,
    }));
}

export async function fetchRunningNeoTraces(
  applicationId: string,
): Promise<BackendRunningNeoTrace[]> {
  const allTraces: BackendRunningNeoTrace[] = [];
  let skip = 0;
  let hasMorePages = true;

  while (hasMorePages) {
    const response = await traceClient.getTracesByApplicationId({
      params: { applicationId },
      query: {
        agentName: 'neo',
        status: 'RUNNING',
        limit: NEO_ATTACK_RUNNING_FETCH_LIMIT,
        skip,
      },
      overrideClientOptions: {
        baseUrl: `${getCurrentGatewayUrl()}/ai`,
      },
    });

    if (response.status !== 200) {
      throw new Error('Failed to fetch running Neo traces');
    }

    const { traces, totalCount } = response.body;
    allTraces.push(...mapRunningNeoTraces(traces));

    hasMorePages =
      traces.length > 0 &&
      skip + traces.length < totalCount &&
      traces.length >= NEO_ATTACK_RUNNING_FETCH_LIMIT;

    if (hasMorePages) {
      skip += traces.length;
    }
  }

  return allTraces;
}
